/**
 * Helpers purs de l'éditeur : manipulation de listes, lecture des chemins
 * d'erreur de `validerScorecard`, message d'une erreur Convex.
 */

import type { ProblemeScorecard } from "@repo/ent-struct";

export function remplacer<T>(liste: T[], index: number, valeur: T): T[] {
  return liste.map((x, i) => (i === index ? valeur : x));
}

export function retirer<T>(liste: T[], index: number): T[] {
  return liste.filter((_, i) => i !== index);
}

/** Échange l'élément `index` avec son voisin (`sens` = -1 : vers le haut). */
export function deplacer<T>(liste: T[], index: number, sens: -1 | 1): T[] {
  const cible = index + sens;
  if (cible < 0 || cible >= liste.length) return liste;
  const copie = [...liste];
  [copie[index], copie[cible]] = [copie[cible] as T, copie[index] as T];
  return copie;
}

/** Chemins sans indice : le contenu entier ou un champ de tête. */
const LIBELLES_CHAMP: Record<string, string> = {
  titre: "Titre",
  competences: "Compétences",
};

const LIBELLES_SEGMENT: Record<string, string> = {
  competences: "Compétence",
  sousCompetences: "Sous-compétence",
  questions: "Question",
  criteres: "Critère",
};

/**
 * `competences[1].questions[0]` → « Compétence 2 › Question 1 ». Un chemin
 * sans indice (`titre`, `competences`) garde son nom.
 */
export function libelleChemin(chemin: string): string {
  const segments = chemin.split(".").map((segment) => {
    const m = /^(\w+)\[(\d+)\]$/.exec(segment);
    if (!m) return LIBELLES_CHAMP[segment] ?? segment;
    return `${LIBELLES_SEGMENT[m[1] ?? ""] ?? m[1]} ${Number(m[2]) + 1}`;
  });
  return segments.join(" › ");
}

/** Messages de chaque élément, par chemin exact : chaque carte lit le sien. */
export function indexerProblemes(
  problemes: ProblemeScorecard[],
): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const { chemin, message } of problemes) {
    index.set(chemin, [...(index.get(chemin) ?? []), message]);
  }
  return index;
}

/**
 * Message lisible d'une erreur levée par une fonction Convex : retire
 * l'enveloppe `[CONVEX M(…)] [Request ID: …] Server Error Uncaught Error:` et
 * la pile qui suit.
 */
export function messageErreur(erreur: unknown): string {
  const brut = erreur instanceof Error ? erreur.message : String(erreur);
  const sansEnveloppe = brut.replace(/^[\s\S]*?Uncaught Error:\s*/, "");
  return sansEnveloppe.split(/\n\s*at /)[0]?.trim() || brut;
}
